<script lang="ts" module>
	export const DEV_CART_PREFILL_PARAM = 'prefill_cart';
	export const DEV_CART_PREFILL_BOOK_SLUG = 'nicola-green-in-seven-days';
	export const DEV_CART_PREFILL_COUPON_CODE = 'SEVENDAYS15';
</script>

<script lang="ts">
	import { onMount } from 'svelte';
	import type {
		CartItem,
		Code,
	} from '#lib/features/checkout/utils/createCartItemsStore/index.js';
	import { getCheckoutContext } from '#lib/features/checkout/utils/getCheckoutContext/index.js';
	import { fetchCart } from '#lib/features/checkout/utils/upsertCart/index.js';
	import { page } from '$app/state';
	import { replaceShallowState } from '$global/utils/replaceShallowState/replaceShallowState';

	interface Props {
		slug: string | null | undefined;
		productId: string | null | undefined;
		cartItem: Omit<CartItem, 'quantity'>;
	}

	let { slug, productId, cartItem }: Props = $props();

	const {
		cartPlatform,
		cartItems: { clearCart, addToCart, addCode, cartId },
		cartDrawer: {
			states: { open },
		},
		mixpanelEvents,
	} = getCheckoutContext();

	// The cart drawer drops a stored code that has no discount amount, so the
	// coupon goes through the cart API (as the drawer's "apply" does) to get the
	// amount before it is stored. It is kept only when the API confirms it
	// covers this book.
	const getCoupon = async (bookProductId: string): Promise<Code | null> => {
		try {
			const response = await fetchCart(
				{
					coupon_code: DEV_CART_PREFILL_COUPON_CODE,
					cartItems: { [bookProductId]: { ...cartItem, quantity: 1 } },
				},
				{ cartPlatform, cartId }
			);

			const products: string[] = response?.coupon?.products || [];

			if (
				response?.coupon?.reason ||
				!response?.coupon?.code ||
				!products.includes(bookProductId)
			) {
				return null;
			}

			return {
				code: response.coupon.code,
				amount: response.amount.discount,
				products,
			};
		} catch {
			return null;
		}
	};

	// Dropped once the cart is filled, so that reloading the page or sharing
	// its address does not empty the cart again.
	const removePrefillParam = () => {
		const url = new URL(page.url.href);
		url.searchParams.delete(DEV_CART_PREFILL_PARAM);

		replaceShallowState(url, page.state);
	};

	// The coupon is stored before the book is added: adding the book starts the
	// drawer's debounced cart upsert, which sends whatever code is stored at
	// that moment. If the book went in first, an upsert sent before the coupon
	// call came back would create a second cart without the coupon, and its
	// response would overwrite the stored discount with 0.
	const prefillCart = async (bookProductId: string) => {
		const coupon = await getCoupon(bookProductId);

		if (coupon) {
			addCode(coupon);
		}

		addToCart(bookProductId, cartItem, 1, mixpanelEvents);
		open.set(true);
		removePrefillParam();
	};

	// Runs on the client only: the PDP is ISR-cached by pathname, so the server
	// never sees the query string.
	onMount(() => {
		if (
			slug !== DEV_CART_PREFILL_BOOK_SLUG ||
			!productId ||
			page.url.searchParams.get(DEV_CART_PREFILL_PARAM) !==
				DEV_CART_PREFILL_COUPON_CODE
		) {
			return;
		}

		clearCart();
		prefillCart(productId);
	});
</script>
